import { type FC, memo, type MouseEvent, useState } from "react";
import { CellWithSubtitle } from "../../../../ProductDetails/components/DataTables/CellWithSubtitle";
import Checkbox from "../../../../common/components/Checkbox";
import OrderTableRow from "./OrderTableRow";

import { SortDirection, useSorting } from "../../../../common/hooks/useSorting";

import {
  Table,
  TableCell,
  TableRow,
  TableSortLabel,
  TableHead,
} from "../../../../common/components/Table/TableElements";
import { type DetailedOrderData, type OrderLineItemData } from "../../../common/modelsV2";
import { useTranslation } from "react-i18next";
import { type CaseDataObject } from "../ServiceCases/types";
interface OrderTableProps {
  className?: string;
  selected: number[];
  selectItem: (itemIndex: number) => void;
  selectAll: (newVal: boolean) => void;
  selectable: boolean;
  orderDetailsData: DetailedOrderData;
  serviceCaseData?: Record<string, CaseDataObject[]>;
}

enum SortKinds {
  SalesOrderNo,
  ProductName,
  Quantity,
  Packageing,
  DeliveryDate,
  Weight,
  Price,
  Total,
  Status,
}

const SORT_HANDLERS: Dict<(a: OrderLineItemData, b: OrderLineItemData) => number> = {
  [SortKinds.SalesOrderNo]: (a, b) => a.lineNumber - b.lineNumber,
  [SortKinds.ProductName]: (a, b) => a.product.designation.localeCompare(b.product.designation),
  [SortKinds.Quantity]: (a, b) => a.qty - b.qty,
  [SortKinds.Packageing]: (a, b) => {
    if (a.packSize === undefined && b.packSize === undefined) {
      return 0;
    }
    if (a.packSize === undefined) {
      return 1;
    }
    if (b.packSize === undefined) {
      return -1;
    }
    return a.packSize - b.packSize;
  },
  [SortKinds.DeliveryDate]: (a, b) => {
    const aTime = a.deliveryDate?.getTime() || 0;
    const bTime = b.deliveryDate?.getTime() || 0;
    return aTime === bTime ? 0 : aTime > bTime ? 1 : -1;
  },
  [SortKinds.Weight]: (a, b) => a.weight.value - b.weight.value,
  [SortKinds.Price]: (a, b) => a.netPrice.value - b.netPrice.value,
  [SortKinds.Total]: (a, b) => a.netPrice.value * a.qty - b.netPrice.value * b.qty,
  [SortKinds.Status]: (a, b) => a.status.localeCompare(b.status),
};

const OrderTable: FC<OrderTableProps> = ({
  className,
  selectable,
  selected,
  selectAll,
  selectItem,
  orderDetailsData,
  serviceCaseData,
}) => {
  const isAllSelected = orderDetailsData.products.filter((p) => !!p.product.url).length === selected.length;

  const selectedMap = selected.reduce((map, index) => {
    map[index] = true;
    return map;
  }, {});

  const [sortCode, setSortCode] = useState(SortKinds.SalesOrderNo);
  const [sortDirection, setSortDirection] = useState(SortDirection.Asc);

  const rows = useSorting(orderDetailsData.products, SORT_HANDLERS, sortCode, sortDirection);

  const createSortHandler = (codeToSet: SortKinds) => (e: MouseEvent<unknown>) => {
    if (codeToSet === sortCode) {
      setSortDirection(sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc);
    } else {
      setSortCode(codeToSet);
    }
  };

  const createTableProps = (boundSortCode: SortKinds) => ({
    onClick: createSortHandler(boundSortCode),
    active: boundSortCode === sortCode,
    direction: boundSortCode === sortCode ? sortDirection : SortDirection.Asc,
  });

  return (
    <div className={`scha__order-table ${className ? className : ""}`}>
      <Table className={"multi-body"}>
        <TableHead>
          <Header
            isAllSelected={isAllSelected}
            selectAll={selectAll}
            selectable={selectable}
            createTableProps={createTableProps}
          />
        </TableHead>
        {rows.map((entry, index) => (
          <OrderTableRow
            key={index}
            rowNum={index}
            data={entry}
            showCheckbox={selectable}
            selected={!!selectedMap[index]}
            selectItem={selectItem}
            soNumber={orderDetailsData.soNumber}
            poNumber={orderDetailsData.poNumber}
            orderData={orderDetailsData}
            serviceCase={serviceCaseData?.[entry.lineNo]}
          />
        ))}
      </Table>
    </div>
  );
};

export default memo(OrderTable);

type HeaderProps = {
  isAllSelected: boolean;
  selectAll: (newVal: boolean) => void;
  selectable: boolean;
  createTableProps: (boundSortCode: SortKinds) => {
    onClick: (e: MouseEvent<unknown>) => void;
    active: boolean;
    direction: SortDirection;
  };
};
const Header: FC<HeaderProps> = ({ isAllSelected, selectAll, selectable, createTableProps }) => {
  const { t } = useTranslation();

  return (
    <TableRow>
      <TableCell>
        <TableSortLabel {...createTableProps(SortKinds.SalesOrderNo)} data-test-id={"order-line-number"}>
          {t("quoteDetailsPage.summary.orderLineNumber")}
        </TableSortLabel>
      </TableCell>
      <TableCell>
        <TableSortLabel {...createTableProps(SortKinds.ProductName)}>
          {t("quoteDetailsPage.table.header.product")}
        </TableSortLabel>
      </TableCell>
      <TableCell>
        <TableSortLabel {...createTableProps(SortKinds.Quantity)}>
          <CellWithSubtitle
            title={t("quoteListPage.table.header.confirmedQuantity")}
            subtitle={t("quoteListPage.table.header.requestedQuantity")}
          />
        </TableSortLabel>
      </TableCell>
      <TableCell>
        <TableSortLabel {...createTableProps(SortKinds.Packageing)}>
          <CellWithSubtitle
            title={t("quoteDetailsPage.summary.packagingCode")}
            subtitle={t("cartPage.table.header.packaging.subtitle")}
          />
        </TableSortLabel>
      </TableCell>
      <TableCell>
        <TableSortLabel {...createTableProps(SortKinds.DeliveryDate)}>
          <CellWithSubtitle
            title={t("quoteListPage.table.header.estimatedDeliveryDate")}
            subtitle={t("addToCart.contract.rdd")}
          />
        </TableSortLabel>
      </TableCell>
      <TableCell>
        <TableSortLabel {...createTableProps(SortKinds.Weight)}>
          <CellWithSubtitle title={t("text.product.weight")} subtitle={t("orderDetailsPage.labels.weightUnit")} />
        </TableSortLabel>
      </TableCell>
      <TableCell>
        <TableSortLabel {...createTableProps(SortKinds.Price)}>
          <CellWithSubtitle
            title={t("orderDetailsPage.table.header.price")}
            subtitle={t("orderDetailsPage.table.header.priceUnit")}
          />
        </TableSortLabel>
      </TableCell>
      <TableCell>
        <TableSortLabel {...createTableProps(SortKinds.Total)}>
          {t("orderDetailsPage.table.header.total")}
        </TableSortLabel>
      </TableCell>
      <TableCell>
        <TableSortLabel {...createTableProps(SortKinds.Status)}>
          {t("orderDetailsPage.table.header.status")}
        </TableSortLabel>
      </TableCell>
      {selectable && (
        <TableCell>
          <Checkbox checked={isAllSelected} onCheck={selectAll} data-test-id={"select-all"} />
        </TableCell>
      )}
    </TableRow>
  );
};
